import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Headphones, Megaphone, Users, Plus, Trash2, Copy, X } from 'lucide-react';
import { assignmentsAPI, eventsAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import {
  ROLE_LABELS, TEAM_ROLES, SERIES_TEAM_ROLES, PERMISSION_LABELS, DEFAULT_PERMISSIONS,
  STATUS_STYLES, STATUS_LABELS, formatMoney, errMsg,
} from '../lib/roles';

const TABS = [
  { key: 'dj', label: 'Lineup', Icon: Headphones },
  { key: 'promoter', label: 'Promoters', Icon: Megaphone },
  { key: 'team', label: 'Team', Icon: Users },
];

export default function EventTeamPage() {
  const { scopeType, scopeId } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [tab, setTab] = useState('dj');
  const [data, setData] = useState(null);
  const [title, setTitle] = useState('');
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = scopeType === 'series' ? await assignmentsAPI.seriesTeam(scopeId) : await assignmentsAPI.eventTeam(scopeId);
      setData(r.data);
    } catch (e) {
      toast.error(errMsg(e));
      navigate(-1);
    }
  }, [scopeType, scopeId, navigate]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate(`/auth?redirect=${encodeURIComponent(window.location.pathname)}`); return; }
    load();
    eventsAPI.get(scopeId).then(r => setTitle(r.data.title)).catch(() => {});
  }, [authLoading, user, load, scopeId, navigate]);

  const canManage = data?.my_permissions?.includes('manage_team');
  const rows = (data?.assignments || []).filter(a => (tab === 'team' ? !['dj', 'promoter'].includes(a.role) : a.role === tab));

  const remove = async (a) => {
    if (!window.confirm(`Remove ${a.display_name || 'this person'} (${a.role_label})?`)) return;
    try { await assignmentsAPI.remove(a.assignment_id); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  const copy = (text) => navigator.clipboard?.writeText(text).then(() => toast.success('Link copied'));

  if (!data) return <div className="min-h-screen bg-[#050505] pt-24 text-center text-gray-400">Loading…</div>;

  return (
    <div className="min-h-screen bg-[#050505] pt-20 pb-16 page-enter">
      <div className="max-w-4xl mx-auto px-4">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-gray-400 hover:text-white text-sm mb-4">
          <ArrowLeft size={16} /> Back
        </button>
        <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
          <div>
            <p className="text-xs font-bold tracking-widest text-red-500 uppercase">{scopeType === 'series' ? 'Series team' : 'Team & lineup'}</p>
            <h1 className="text-2xl md:text-3xl font-black text-white uppercase">{title || '…'}</h1>
            {scopeType === 'event' && (
              <Link to={`/events/${scopeId}`} className="text-sm text-gray-400 hover:text-red-400">View event page</Link>
            )}
          </div>
          {canManage && (
            <button onClick={() => setAdding(true)} className="btn-primary flex items-center gap-2 px-4 py-2 rounded text-sm">
              <Plus size={16} /> Add {tab === 'dj' ? 'DJ / artist' : tab === 'promoter' ? 'promoter' : 'team member'}
            </button>
          )}
        </div>

        <div className="flex gap-2 mb-6">
          {TABS.map(({ key, label, Icon }) => (
            <button key={key} onClick={() => setTab(key)}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded border text-sm font-bold transition-colors ${tab === key ? 'bg-red-600 border-red-600 text-white' : 'border-gray-800 text-gray-400 hover:text-white'}`}>
              <Icon size={15} /> {label}
            </button>
          ))}
        </div>

        <div className="space-y-3">
          {tab === 'team' && data.owner && (
            <div className="bg-[#0A0A0A] border border-gray-800 rounded-lg p-4">
              <p className="text-white font-semibold">{data.owner.name}</p>
              <p className="text-gray-500 text-sm">Event owner · full control</p>
            </div>
          )}
          {rows.length === 0 && (
            <div className="text-center py-16 text-gray-500">
              {tab === 'dj' ? 'No DJs or artists yet.' : tab === 'promoter' ? 'No promoters assigned yet.' : 'No team members yet.'}
            </div>
          )}
          {rows.map(a => (
            <div key={a.assignment_id} className="bg-[#0A0A0A] border border-gray-800 rounded-lg p-4">
              <div className="flex flex-wrap items-start gap-3">
                <div className="flex-1 min-w-0">
                  {a.user && a.role === 'dj' ? (
                    <Link to={`/artists/${a.user.user_id}`} className="text-white font-semibold hover:text-red-400">{a.display_name}</Link>
                  ) : (
                    <p className="text-white font-semibold">{a.display_name || a.contact_email || 'Invited'}</p>
                  )}
                  <p className="text-gray-500 text-sm">
                    {a.role_label}
                    {a.inherited_from_series ? ' · whole series' : ''}
                    {a.user?.is_ambassador ? ' · SAC ambassador' : ''}
                    {!a.user && a.contact_email ? ` · ${a.contact_email}` : ''}
                  </p>
                  {a.campaign_name && <p className="text-gray-500 text-sm">Campaign: {a.campaign_name}</p>}
                  {a.role === 'promoter' && a.commission_type !== 'none' && (
                    <p className="text-gray-500 text-sm">
                      Commission: {a.commission_type === 'percent' ? `${a.commission_value}% of sales` : `${formatMoney(a.commission_value)} / ticket`}
                    </p>
                  )}
                  {!['dj', 'promoter'].includes(a.role) && (
                    <p className="text-gray-400 text-xs mt-1">
                      {a.permissions.filter(p => p !== 'view').map(p => PERMISSION_LABELS[p] || p).join(' · ') || 'View only'}
                    </p>
                  )}
                </div>
                <span className={`text-[11px] font-bold border rounded-full px-2 py-0.5 ${STATUS_STYLES[a.status]}`}>{STATUS_LABELS[a.status]}</span>
              </div>
              {a.stats && (
                <div className="grid grid-cols-4 gap-2 mt-3 pt-3 border-t border-gray-800 text-center">
                  {[['Clicks', a.stats.clicks], ['Tickets', a.stats.tickets], ['Revenue', formatMoney(a.stats.revenue)], ['Earned', formatMoney(a.stats.earnings)]].map(([l, v]) => (
                    <div key={l}><p className="text-white font-black">{v}</p><p className="text-gray-500 text-[11px]">{l}</p></div>
                  ))}
                </div>
              )}
              {canManage && !a.inherited_from_series && (
                <div className="flex justify-end gap-4 mt-3 text-sm">
                  {a.invite_link && (
                    <button onClick={() => copy(a.invite_link)} className="flex items-center gap-1 text-gray-300 hover:text-white"><Copy size={14} /> Copy invite link</button>
                  )}
                  <button onClick={() => remove(a)} className="flex items-center gap-1 text-red-400 hover:text-red-300"><Trash2 size={14} /> Remove</button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {adding && (
        <AddDialog
          kind={tab}
          scopeType={scopeType}
          scopeId={scopeId}
          myPermissions={data.my_permissions}
          isOwner={data.is_owner || ['super_admin', 'finance_admin', 'support_admin', 'translator_admin'].includes(user?.role)}
          onClose={() => setAdding(false)}
          onAdded={(warning) => { setAdding(false); load(); toast.success('Added'); if (warning) toast.warning(warning); }}
        />
      )}
    </div>
  );
}

const inputCls = 'w-full bg-[#111] border border-gray-700 text-white px-3 py-2.5 rounded text-sm focus:border-red-500 outline-none';

function Chip({ active, onClick, children }) {
  return (
    <button type="button" onClick={onClick}
      className={`px-3 py-1.5 rounded-full border text-xs font-semibold ${active ? 'bg-red-600 border-red-600 text-white' : 'border-gray-700 text-gray-300 hover:border-gray-500'}`}>
      {children}
    </button>
  );
}

function AddDialog({ kind, scopeType, scopeId, myPermissions, isOwner, onClose, onAdded }) {
  const teamRoles = (scopeType === 'series' ? SERIES_TEAM_ROLES : TEAM_ROLES)
    .filter(r => isOwner || !['co_organizer', 'series_manager'].includes(r));
  const [mode, setMode] = useState('existing');
  const [role, setRole] = useState(teamRoles[0]);
  const [perms, setPerms] = useState(DEFAULT_PERMISSIONS[teamRoles[0]] || []);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [picked, setPicked] = useState(null);
  const [f, setF] = useState({ email: '', name: '', instagram: '', campaign: '', commissionType: 'none', commissionValue: '', target: '', deadline: '', materials: '' });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF(prev => ({ ...prev, [k]: e.target.value }));

  useEffect(() => {
    if (mode !== 'existing' || query.trim().length < 2) { setResults([]); return undefined; }
    const capability = kind === 'dj' ? 'dj' : kind === 'promoter' ? 'promoter' : undefined;
    const t = setTimeout(() => assignmentsAPI.searchUsers(query.trim(), capability).then(r => setResults(r.data)).catch(() => setResults([])), 300);
    return () => clearTimeout(t);
  }, [query, mode, kind]);

  const submit = async (e) => {
    e.preventDefault();
    const payload = { scope_type: scopeType, scope_id: scopeId, role: kind === 'team' ? role : kind };
    if (mode === 'existing') {
      if (!picked) return toast.error('Search and select a person first.');
      payload.user_id = picked.user_id;
    } else if (mode === 'email') {
      payload.email = f.email.trim();
      if (f.name.trim()) payload.display_name = f.name.trim();
    } else {
      payload.display_name = f.name.trim();
    }
    if (kind === 'dj' && f.instagram.trim()) payload.social_links = { instagram: f.instagram.trim() };
    if (kind === 'team') payload.permissions = [...perms, 'view'];
    if (kind === 'promoter') {
      if (f.campaign.trim()) payload.campaign_name = f.campaign.trim();
      payload.commission_type = f.commissionType;
      if (f.commissionType !== 'none') payload.commission_value = Number(f.commissionValue) || 0;
      if (f.target) payload.target_tickets = parseInt(f.target, 10);
      if (f.deadline) payload.ends_at = f.deadline;
      const urls = f.materials.split(/[\n,]/).map(x => x.trim()).filter(Boolean);
      if (urls.length) payload.materials = urls;
    }
    setSaving(true);
    try {
      const r = await assignmentsAPI.create(payload);
      onAdded(r.data.warning);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  const modes = kind === 'dj'
    ? [['existing', 'SAC Events DJ'], ['email', 'Invite by email'], ['display', 'Name only (no account)']]
    : [['existing', kind === 'promoter' ? 'SAC Events promoter' : 'SAC Events user'], ['email', 'Invite by email']];

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={e => e.stopPropagation()}
        className="bg-[#0A0A0A] border border-gray-800 rounded-t-xl sm:rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-white font-black uppercase">{kind === 'dj' ? 'Add DJ / artist' : kind === 'promoter' ? 'Assign promoter' : 'Add team member'}</h2>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-white"><X size={18} /></button>
        </div>

        {kind === 'team' && (
          <div>
            <p className="text-[11px] font-black tracking-widest text-gray-500 mb-2">ROLE</p>
            <div className="flex flex-wrap gap-2">
              {teamRoles.map(r => <Chip key={r} active={role === r} onClick={() => { setRole(r); setPerms(DEFAULT_PERMISSIONS[r] || []); }}>{ROLE_LABELS[r]}</Chip>)}
            </div>
          </div>
        )}

        <div>
          <p className="text-[11px] font-black tracking-widest text-gray-500 mb-2">WHO</p>
          <div className="flex flex-wrap gap-2">
            {modes.map(([m, l]) => <Chip key={m} active={mode === m} onClick={() => setMode(m)}>{l}</Chip>)}
          </div>
        </div>

        {mode === 'existing' && (
          <div className="space-y-2">
            <input className={inputCls} value={query} onChange={e => { setQuery(e.target.value); setPicked(null); }} placeholder="Search by name or username" autoFocus />
            {picked ? (
              <p className="text-green-400 text-sm">✓ {picked.name}{picked.username ? ` (@${picked.username})` : ''}</p>
            ) : results.map(u => (
              <button type="button" key={u.user_id} onClick={() => setPicked(u)} className="block w-full text-left px-3 py-2 rounded hover:bg-white/5">
                <span className="text-white text-sm font-semibold">{u.name}</span>
                <span className="text-gray-500 text-xs ml-2">{u.username ? `@${u.username} · ` : ''}{(u.capabilities || []).join(', ') || 'member'}</span>
              </button>
            ))}
            {query.trim().length >= 2 && !picked && results.length === 0 && (
              <p className="text-gray-500 text-xs">No match. {kind === 'dj' ? 'Invite them by email or add their name only.' : 'Invite them by email instead.'}</p>
            )}
          </div>
        )}
        {mode === 'email' && (
          <div className="space-y-2">
            <input className={inputCls} value={f.name} onChange={set('name')} placeholder="Name (optional)" />
            <input className={inputCls} type="email" required value={f.email} onChange={set('email')} placeholder="Email address" />
            <p className="text-gray-500 text-xs">They receive an invitation link to create an account and accept.</p>
          </div>
        )}
        {mode === 'display' && (
          <div className="space-y-2">
            <input className={inputCls} required value={f.name} onChange={set('name')} placeholder="Public DJ / artist name" />
            <p className="text-gray-500 text-xs">Shown on the event poster and listing. No account or invitation needed.</p>
          </div>
        )}
        {kind === 'dj' && mode !== 'existing' && (
          <input className={inputCls} value={f.instagram} onChange={set('instagram')} placeholder="Instagram (optional)" />
        )}

        {kind === 'team' && role !== 'viewer' && (
          <div>
            <p className="text-[11px] font-black tracking-widest text-gray-500 mb-2">PERMISSIONS</p>
            {Object.entries(PERMISSION_LABELS).filter(([p]) => myPermissions.includes(p)).map(([p, l]) => (
              <label key={p} className="flex items-center justify-between py-1.5 text-sm text-gray-200 cursor-pointer">
                {l}
                <input type="checkbox" className="accent-red-600 w-4 h-4" checked={perms.includes(p)}
                  onChange={() => setPerms(prev => prev.includes(p) ? prev.filter(x => x !== p) : [...prev, p])} />
              </label>
            ))}
          </div>
        )}

        {kind === 'promoter' && (
          <div className="space-y-2">
            <p className="text-[11px] font-black tracking-widest text-gray-500">CAMPAIGN</p>
            <input className={inputCls} value={f.campaign} onChange={set('campaign')} placeholder="Campaign name (optional)" />
            <div className="flex flex-wrap gap-2">
              {[['none', 'No commission'], ['percent', '% of sales'], ['per_ticket', '€ per ticket']].map(([v, l]) => (
                <Chip key={v} active={f.commissionType === v} onClick={() => setF(p => ({ ...p, commissionType: v }))}>{l}</Chip>
              ))}
            </div>
            {f.commissionType !== 'none' && (
              <input className={inputCls} type="number" min="0" step="0.01" value={f.commissionValue} onChange={set('commissionValue')} placeholder={f.commissionType === 'percent' ? 'e.g. 10' : 'e.g. 2.00'} />
            )}
            <div className="grid grid-cols-2 gap-2">
              <input className={inputCls} type="number" min="1" value={f.target} onChange={set('target')} placeholder="Ticket target" />
              <input className={inputCls} type="date" value={f.deadline} onChange={set('deadline')} title="Deadline" />
            </div>
            <textarea className={inputCls} rows={3} value={f.materials} onChange={set('materials')} placeholder="Poster / video links, one per line (optional)" />
            <p className="text-gray-500 text-xs">After accepting, the promoter gets a unique tracking link and QR code. Sales are measured by SAC Events itself, never via social-network logins.</p>
          </div>
        )}

        <button type="submit" disabled={saving} className="btn-primary w-full py-3 rounded font-black disabled:opacity-60">
          {saving ? 'Saving…' : mode === 'display' ? 'Add to lineup' : 'Send invitation'}
        </button>
      </form>
    </div>
  );
}
